import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { registerLineChannel, type RegisterLineChannelRequest } from 'api/line';
import { queryKeys } from 'constants/queryKeys';
import { lineStrings } from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import type { TLineChannelOperationResponse } from 'types';
import { toErrorMessage } from 'hooks/utils';

type UseRegisterLineChannelOptions = {
  onSuccess?: (response: TLineChannelOperationResponse) => void;
};

type UseRegisterLineChannelResult = {
  register: (request: RegisterLineChannelRequest) => void;
  isSubmitting: boolean;
  error: string | undefined;
};

export function useRegisterLineChannel(
  options: UseRegisterLineChannelOptions = {},
): UseRegisterLineChannelResult {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const strings = lineStrings.connect;

  const [error, setError] = useState<string | undefined>();

  const mutation = useMutation({
    mutationFn: async (request: RegisterLineChannelRequest) => {
      if (!user) {
        throw new Error(strings.errors.unauthorized);
      }
      return registerLineChannel(request);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.LINE_CHANNEL_ACCOUNTS, user?.uid],
      });
      setError(undefined);
      options.onSuccess?.(response);
    },
    onError: (caught) => {
      setError(toErrorMessage(caught, strings.errors.submitFailed));
    },
  });

  const register = (request: RegisterLineChannelRequest) => {
    setError(undefined);
    mutation.mutate(request);
  };

  return {
    register,
    isSubmitting: mutation.isPending,
    error,
  };
}
